import React, { useState } from 'react';
import { isWeekend, isHoliday, getDayName, formatDate, formatDateLong, formatCurrency } from '../utils/holidays';
import * as XLSX from 'xlsx';

const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

function TravelCosts({ month, year, registrations, customDistances, zakelijkeReizen, userSettings, userProfile, afasSubmissions, setAfasSubmissions, ovBedragen, section = 'woonwerk' }) {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const isSubmitted = afasSubmissions[monthKey]?.reiskosten || false;
  const submittedDate = afasSubmissions[monthKey]?.reisDatum || null;
  const isSubmittedZakelijk = afasSubmissions[monthKey]?.zakelijkeReizen || false;
  const submittedDateZakelijk = afasSubmissions[monthKey]?.zakelijkeDatum || null;

  const handleAfasToggle = () => {
    setAfasSubmissions(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        reiskosten: !isSubmitted,
        reisDatum: !isSubmitted ? new Date().toLocaleDateString('nl-NL') : null
      }
    }));
  };

  const handleAfasToggleZakelijk = () => {
    setAfasSubmissions(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        zakelijkeReizen: !isSubmittedZakelijk,
        zakelijkeDatum: !isSubmittedZakelijk ? new Date().toLocaleDateString('nl-NL') : null
      }
    }));
  };

  const [copyToast, setCopyToast] = useState(false);
  const [reisFilter, setReisFilter] = useState('all');
  const [zakelijkFilter, setZakelijkFilter] = useState('all');

  // Get all travel days in the month (from registrations, incl. standaard-registratiedagen — zelfde fallback als Summary)
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthRegistrations = registrations[monthKey] || {};
  const monthCustomDistances = customDistances[monthKey] || {};
  const travelDays = [];
  const onder10KmDagen = [];

  const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
  const defaultThuiswerkDagen = userSettings.defaultThuiswerkDagen || [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(Date.UTC(year, month, day));
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayNum = date.getDay();
    const isWerkdag = werkdagen.includes(dayNum);
    const isAutoVrij = isWeekend(date) || isHoliday(date, year) || !isWerkdag;
    const customDefault = (userSettings.defaultRegistratieDagen || {})[dayNum];
    const defaultDag = !isAutoVrij
      ? (customDefault !== undefined ? customDefault : (defaultThuiswerkDagen.includes(dayNum) ? 'thuis' : ''))
      : '';
    const registration = monthRegistrations[dateString] ?? (isAutoVrij ? 'vrij' : defaultDag);

    if ((registration === 'fiets' || registration === 'auto') && !isWeekend(date) && !isHoliday(date, year)) {
      const enkeleKm = parseFloat(monthCustomDistances[dateString] || (registration === 'auto' ? userSettings.kmPerRitAuto : userSettings.kmPerRitFiets)) || 0;
      if (enkeleKm <= 10) { onder10KmDagen.push(dateString); continue; }
      travelDays.push({
        date,
        dateString,
        formattedDate: formatDate(date),
        dayName: getDayName(date),
        enkeleKm,
        km: enkeleKm * 2,
        description: registration === 'auto' ? 'Woon-werk verkeer auto' : 'Woon-werk verkeer fiets',
        type: registration
      });
    }
    if (registration === 'ov' && !isWeekend(date) && !isHoliday(date, year) && !(userSettings.heeftNsBusinesscard ?? true)) {
      const bedrag = ovBedragen?.[monthKey]?.[dateString] || 0;
      if (bedrag > 0) {
        travelDays.push({
          date,
          dateString,
          formattedDate: formatDate(date),
          dayName: getDayName(date),
          enkeleKm: 0,
          km: 0,
          bedrag,
          description: 'Woon-werk OV',
          type: 'ov'
        });
      }
    }
  }

  // Get zakelijke reizen for this month (supports legacy number, array-of-{id,km}, and OV {id,type,bedrag})
  const monthZakelijkeReizen = zakelijkeReizen?.[monthKey] || {};
  const zakelijkeDagen = Object.entries(monthZakelijkeReizen)
    .flatMap(([dateString, val]) => {
      const date = new Date(dateString + 'T00:00:00Z');
      const ritten = Array.isArray(val) ? val : (val > 0 ? [{ id: 'legacy', km: val }] : []);
      return ritten.map(rit => ({
        id: `zakelijk-${dateString}-${rit.id}`,
        date,
        dateString,
        formattedDate: formatDate(date),
        dayName: getDayName(date),
        ritType: rit.type || 'auto',
        km: rit.km || 0,
        bedrag: rit.bedrag || 0,
        description: rit.type === 'ov' ? 'Zakelijke reis (OV)' : 'Zakelijke reis',
      }));
    })
    .filter(d => d.km > 0 || d.bedrag > 0)
    .sort((a, b) => a.date - b.date);

  const totalKmZakelijk = zakelijkeDagen.filter(d => d.ritType !== 'ov').reduce((sum, d) => sum + d.km, 0);
  const totalBedragOvZakelijk = zakelijkeDagen.filter(d => d.ritType === 'ov').reduce((sum, d) => sum + d.bedrag, 0);
  const totalCompensationZakelijk = totalKmZakelijk * (userSettings.vergoedingPerKmZakelijk ?? 0.23) + totalBedragOvZakelijk;

  const allTrips = [...travelDays].sort((a, b) => a.date - b.date);

  const totalKm = allTrips.filter(t => t.type !== 'ov').reduce((sum, trip) => sum + parseFloat(trip.km), 0);
  const totalOvBedrag = allTrips.filter(t => t.type === 'ov').reduce((sum, trip) => sum + (trip.bedrag || 0), 0);
  const totalCompensation = totalKm * userSettings.vergoedingPerKm + totalOvBedrag;

  const kanOvHebben = !(userSettings.heeftNsBusinesscard ?? true);

  const filteredZakelijkeDagen = kanOvHebben ? zakelijkeDagen.filter(dag => {
    if (zakelijkFilter === 'auto') return dag.ritType !== 'ov';
    if (zakelijkFilter === 'ov') return dag.ritType === 'ov';
    return true;
  }) : zakelijkeDagen;
  const displayTotalKmZakelijk = filteredZakelijkeDagen.filter(d => d.ritType !== 'ov').reduce((sum, d) => sum + d.km, 0);
  const displayTotalBedragOvZakelijk = filteredZakelijkeDagen.filter(d => d.ritType === 'ov').reduce((sum, d) => sum + d.bedrag, 0);
  const displayTotalCompensationZakelijk = displayTotalKmZakelijk * (userSettings.vergoedingPerKmZakelijk ?? 0.23) + displayTotalBedragOvZakelijk;
  const filteredTrips = (section === 'woonwerk' && kanOvHebben) ? allTrips.filter(trip => {
    if (reisFilter === 'auto') return trip.type !== 'ov';
    if (reisFilter === 'ov') return trip.type === 'ov';
    return true;
  }) : allTrips;
  const displayTotalKm = filteredTrips.filter(t => t.type !== 'ov').reduce((sum, t) => sum + t.km, 0);
  const displayTotalOvBedrag = filteredTrips.filter(t => t.type === 'ov').reduce((sum, t) => sum + (t.bedrag || 0), 0);
  const displayTotalCompensation = displayTotalKm * userSettings.vergoedingPerKm + displayTotalOvBedrag;

  const volledigeNaam = [userProfile?.voornaam, userProfile?.achternaam].filter(Boolean).join(' ') || '—';
  const gebruikteTypes = [...new Set(allTrips.map(t => t.type).filter(t => t === 'auto' || t === 'fiets'))];
  const voertuigLabel = gebruikteTypes.length === 0 ? '—'
    : gebruikteTypes.length === 2 ? 'Auto / Fiets'
    : gebruikteTypes[0] === 'auto' ? 'Auto' : 'Fiets';

  const downloadExcel = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const rows = [
      [`Reiskosten - ${maandNaam}`],
      [],
      ['Totaal km', totalKm],
      ['Vergoeding per km', userSettings.vergoedingPerKm],
      ['Totaal vergoeding', totalCompensation],
      [],
      ['Datum', 'Dag', 'Week', 'Omschrijving', 'Type', 'Aantal km', 'Vergoeding'],
      ...allTrips.map(trip => [
        trip.formattedDate,
        trip.dayName,
        getWeekNumber(trip.date),
        trip.description,
        trip.type === 'auto' ? 'Auto' : 'Fiets',
        trip.km,
        trip.km * userSettings.vergoedingPerKm,
      ]),
      ['Totaal', '', '', '', '', totalKm, totalCompensation],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 28 }, { wch: 8 }, { wch: 12 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reiskosten');
    XLSX.writeFile(wb, `Reiskosten_${maandNaam.replace(' ', '_')}.xlsx`);
  };

  const showCopyToast = () => { setCopyToast(true); setTimeout(() => setCopyToast(false), 3000); };

  const downloadExcelZakelijk = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const rows = [
      [`Zakelijke reizen - ${maandNaam}`],
      [],
      ['Totaal km', displayTotalKmZakelijk],
      ['Vergoeding per km', userSettings.vergoedingPerKmZakelijk ?? 0.23],
      ['Totaal OV', displayTotalBedragOvZakelijk],
      ['Totaal vergoeding', displayTotalCompensationZakelijk],
      [],
      ['Datum', 'Dag', 'Omschrijving', 'Km / OV', 'Vergoeding'],
      ...filteredZakelijkeDagen.map(dag => [
        dag.formattedDate,
        dag.dayName,
        dag.description,
        dag.ritType === 'ov' ? `OV €${dag.bedrag.toFixed(2)}` : dag.km,
        dag.ritType === 'ov' ? dag.bedrag : dag.km * (userSettings.vergoedingPerKmZakelijk ?? 0.23),
      ]),
      ['Totaal', '', '', '', displayTotalCompensationZakelijk],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Zakelijke reizen');
    XLSX.writeFile(wb, `Zakelijke_reizen_${maandNaam.replace(' ', '_')}.xlsx`);
  };

  const copyZakelijkToClipboard = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const filterLabel = zakelijkFilter === 'ov' ? ' — OV' : zakelijkFilter === 'auto' ? ' — Auto / Fiets' : '';
    const text = `Zakelijke reizen ${maandNaam}${filterLabel}\n\n${filteredZakelijkeDagen.map(dag => {
      const reiswijze = dag.ritType === 'ov' ? 'OV' : `${dag.km.toFixed(2).replace('.', ',')} km`;
      const vergoeding = dag.ritType === 'ov'
        ? formatCurrency(dag.bedrag)
        : formatCurrency(dag.km * (userSettings.vergoedingPerKmZakelijk ?? 0.23));
      return `${dag.dayName} ${formatDateLong(dag.date)} | ${reiswijze} | ${vergoeding}`;
    }).join('\n')}\n\nTotaal vergoeding: ${formatCurrency(displayTotalCompensationZakelijk)}`;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopyToast);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showCopyToast();
    }
  };

  const copyToClipboard = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const filteredTypes = [...new Set(filteredTrips.map(t => t.type).filter(t => t === 'auto' || t === 'fiets'))];
    const filteredVoertuigLabel = filteredTypes.length === 2 ? 'Auto / Fiets'
      : filteredTypes[0] === 'auto' ? 'Auto'
      : filteredTypes[0] === 'fiets' ? 'Fiets' : '';
    const headerType = reisFilter === 'ov' ? 'OV' : filteredVoertuigLabel;
    const header = headerType ? `Woon-werk-verkeer | ${headerType}` : 'Woon-werk-verkeer';
    const lines = filteredTrips.map(trip => {
      if (trip.type === 'ov') {
        return `${trip.dayName} ${formatDateLong(trip.date)} | OV | ${formatCurrency(trip.bedrag)}`;
      }
      const typeLabel = trip.type === 'auto' ? 'Auto' : 'Fiets';
      return `${trip.dayName} ${formatDateLong(trip.date)} | ${typeLabel} | ${trip.km.toFixed(2).replace('.', ',')} km`;
    });
    const kmRegel = displayTotalKm > 0 ? `Totaal aantal kilometers: ${displayTotalKm.toFixed(2).replace('.', ',')} km\n` : '';
    const ovRegel = displayTotalOvBedrag > 0 ? `Totaal bedrag OV: ${formatCurrency(displayTotalOvBedrag)}\n` : '';
    const text = `${header} ${maandNaam}\n${kmRegel}${ovRegel}\n${lines.join('\n')}`;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopyToast);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showCopyToast();
    }
  };

  return (
    <>
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            {section === 'zakelijk' ? 'Zakelijke reizen' : 'Woon-werk-verkeer'} — {new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center space-x-2">
            {section !== 'zakelijk' && isSubmitted && (
              <span className="text-xs text-green-700">Ingediend op {submittedDate}</span>
            )}
            {section !== 'zakelijk' && (
              <button
                onClick={handleAfasToggle}
                className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isSubmitted
                    ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400'
                }`}
              >
                {isSubmitted ? '✓ Ingediend in AFAS' : 'Ingediend in AFAS'}
              </button>
            )}
            {section === 'zakelijk' && isSubmittedZakelijk && (
              <span className="text-xs text-green-700">Ingediend op {submittedDateZakelijk}</span>
            )}
            {section === 'zakelijk' && (
              <button
                onClick={handleAfasToggleZakelijk}
                className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isSubmittedZakelijk
                    ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400'
                }`}
              >
                {isSubmittedZakelijk ? '✓ Ingediend in AFAS' : 'Ingediend in AFAS'}
              </button>
            )}
            <button
              onClick={section === 'zakelijk' ? copyZakelijkToClipboard : copyToClipboard}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
            >
              Kopie AFAS
            </button>
          </div>
        </div>

        {/* Summary */}
        {section !== 'zakelijk' && (
        <div className="mb-6">
          {reisFilter === 'all' && kanOvHebben && displayTotalKm > 0 && displayTotalOvBedrag > 0 ? (
            /* Split view: both Auto/Fiets and OV present */
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">{voertuigLabel}</p>
                <div className="text-xs text-gray-500 flex gap-6 mb-1">
                  <span className="w-24">Totaal kilometers</span>
                  <span className="w-20">Vergoeding</span>
                  <span>Totaal bedrag</span>
                </div>
                <div className="text-sm text-gray-700 flex gap-6">
                  <span className="font-medium w-24">{displayTotalKm.toFixed(2).replace('.', ',')} km</span>
                  <span className="font-medium w-20">{formatCurrency(userSettings.vergoedingPerKm)}</span>
                  <span className="font-bold">{formatCurrency(displayTotalKm * userSettings.vergoedingPerKm)}</span>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">OV</p>
                <div className="text-xs text-gray-500 mb-1">Totaal bedrag</div>
                <div className="text-sm font-bold text-gray-700">{formatCurrency(displayTotalOvBedrag)}</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 flex flex-col justify-center">
                <p className="text-xs text-yellow-700 mb-1">Totaal vergoeding</p>
                <p className="text-base font-semibold text-yellow-900">{formatCurrency(displayTotalCompensation)}</p>
              </div>
            </div>
          ) : (
            /* Single-type or filtered view */
            <div className="flex gap-3">
              {reisFilter !== 'ov' && displayTotalKm > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 flex-1">
                  <p className="text-xs text-blue-600 mb-1">Totaal kilometers</p>
                  <p className="text-sm font-semibold text-blue-900">{displayTotalKm.toFixed(2).replace('.', ',')} km</p>
                </div>
              )}
              {reisFilter !== 'ov' && displayTotalKm > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 flex-1">
                  <p className="text-xs text-blue-600 mb-1">Vergoeding per km</p>
                  <p className="text-sm font-semibold text-blue-900">{formatCurrency(userSettings.vergoedingPerKm)}</p>
                </div>
              )}
              {reisFilter !== 'auto' && displayTotalOvBedrag > 0 && (
                <div className="bg-green-50 rounded-lg p-4 flex-1">
                  <p className="text-xs text-green-600 mb-1">OV declaraties</p>
                  <p className="text-sm font-semibold text-green-900">{formatCurrency(displayTotalOvBedrag)}</p>
                </div>
              )}
              <div className="bg-yellow-50 rounded-lg p-4 flex-1">
                <p className="text-xs text-yellow-700 mb-1">Totaal vergoeding</p>
                <p className="text-sm font-semibold text-yellow-900">{formatCurrency(displayTotalCompensation)}</p>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Filter tabs + Excel */}
        {section !== 'zakelijk' && (
          <div className="mb-4 flex items-center border-b border-gray-200">
            {kanOvHebben && [
              { id: 'all', label: 'Alles' },
              { id: 'auto', label: 'Auto / Fiets' },
              { id: 'ov', label: 'OV' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setReisFilter(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                  reisFilter === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
            {filteredTrips.length > 0 && (
              <button onClick={downloadExcel} className="ml-auto flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition pb-2.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Excel
              </button>
            )}
          </div>
        )}
        {section !== 'zakelijk' && (filteredTrips.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Week
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Retour km
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Omschrijving
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vergoeding
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTrips.map((trip) => (
                  <tr key={trip.id || trip.dateString}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trip.dayName} {trip.formattedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getWeekNumber(trip.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trip.type === 'ov' ? (
                        <span className="text-green-700 font-medium">OV</span>
                      ) : (
                        <>
                          <span>{trip.km.toFixed(2).replace('.', ',')} km</span>
                          {trip.enkeleKm > 0 && (
                            <span className="block text-xs text-gray-400">enkel: {trip.enkeleKm.toFixed(2).replace('.', ',')} km</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trip.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        trip.type === 'ov' ? 'bg-green-100 text-green-800'
                        : trip.type === 'auto' ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                      }`}>
                        {trip.type === 'ov' ? 'OV' : trip.type === 'auto' ? 'Auto' : 'Fiets'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {trip.type === 'ov'
                        ? formatCurrency(trip.bedrag)
                        : formatCurrency(trip.km * userSettings.vergoedingPerKm)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Totaal</td>
                  <td></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {displayTotalKm > 0 ? `${displayTotalKm.toFixed(2).replace('.', ',')} km` : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" colSpan="2"></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(displayTotalCompensation)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            {reisFilter === 'ov' ? 'Geen OV-reizen geregistreerd voor deze maand.' :
             reisFilter === 'auto' ? 'Geen auto- of fietsreizen geregistreerd voor deze maand.' :
             'Geen reizen geregistreerd voor deze maand.'}
            {reisFilter === 'all' && <><br />Registreer dagen als "fiets" of "auto" in de agenda of voeg handmatige reizen toe.</>}
          </p>
        ))}
        {section !== 'zakelijk' && onder10KmDagen.length > 0 && (
          <p className="mt-3 text-xs text-gray-500">
            ℹ️ {onder10KmDagen.length} fiets-/autodag{onder10KmDagen.length !== 1 ? 'en' : ''} niet meegeteld: enkele reisafstand is 10 km of minder, daarvoor geldt geen vergoeding.
          </p>
        )}

        {/* Zakelijke reizen */}
        {section === 'zakelijk' && (
          <>
            {zakelijkeDagen.length > 0 && (
              <div className="mb-6">
                {zakelijkFilter === 'all' && kanOvHebben && displayTotalKmZakelijk > 0 && displayTotalBedragOvZakelijk > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">Auto / Fiets</p>
                      <div className="text-xs text-gray-500 flex gap-6 mb-1">
                        <span className="w-24">Totaal kilometers</span>
                        <span className="w-20">Vergoeding</span>
                        <span>Totaal bedrag</span>
                      </div>
                      <div className="text-sm text-gray-700 flex gap-6">
                        <span className="font-medium w-24">{displayTotalKmZakelijk.toFixed(2).replace('.', ',')} km</span>
                        <span className="font-medium w-20">{formatCurrency(userSettings.vergoedingPerKmZakelijk ?? 0.23)}</span>
                        <span className="font-bold">{formatCurrency(displayTotalKmZakelijk * (userSettings.vergoedingPerKmZakelijk ?? 0.23))}</span>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">OV</p>
                      <div className="text-xs text-gray-500 mb-1">Totaal bedrag</div>
                      <div className="text-sm font-bold text-gray-700">{formatCurrency(displayTotalBedragOvZakelijk)}</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 flex flex-col justify-center">
                      <p className="text-xs text-yellow-700 mb-1">Totaal vergoeding</p>
                      <p className="text-base font-semibold text-yellow-900">{formatCurrency(displayTotalCompensationZakelijk)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    {zakelijkFilter !== 'ov' && displayTotalKmZakelijk > 0 && (
                      <div className="bg-blue-50 rounded-lg p-4 flex-1">
                        <p className="text-xs text-blue-600 mb-1">Totaal kilometers</p>
                        <p className="text-sm font-semibold text-blue-900">{displayTotalKmZakelijk.toFixed(2).replace('.', ',')} km</p>
                      </div>
                    )}
                    {zakelijkFilter !== 'ov' && displayTotalKmZakelijk > 0 && (
                      <div className="bg-blue-50 rounded-lg p-4 flex-1">
                        <p className="text-xs text-blue-600 mb-1">Vergoeding per km</p>
                        <p className="text-sm font-semibold text-blue-900">{formatCurrency(userSettings.vergoedingPerKmZakelijk ?? 0.23)}</p>
                      </div>
                    )}
                    {zakelijkFilter !== 'auto' && displayTotalBedragOvZakelijk > 0 && (
                      <div className="bg-green-50 rounded-lg p-4 flex-1">
                        <p className="text-xs text-green-600 mb-1">OV declaraties</p>
                        <p className="text-sm font-semibold text-green-900">{formatCurrency(displayTotalBedragOvZakelijk)}</p>
                      </div>
                    )}
                    <div className="bg-yellow-50 rounded-lg p-4 flex-1">
                      <p className="text-xs text-yellow-700 mb-1">Totaal vergoeding</p>
                      <p className="text-sm font-semibold text-yellow-900">{formatCurrency(displayTotalCompensationZakelijk)}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-4 flex items-center border-b border-gray-200">
              {kanOvHebben && [
                { id: 'all', label: 'Alles' },
                { id: 'auto', label: 'Auto / Fiets' },
                { id: 'ov', label: 'OV' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setZakelijkFilter(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                    zakelijkFilter === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              {filteredZakelijkeDagen.length > 0 && (
                <button onClick={downloadExcelZakelijk} className="ml-auto flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition pb-2.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Excel
                </button>
              )}
            </div>

            {filteredZakelijkeDagen.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Omschrijving</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Km / OV</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vergoeding</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredZakelijkeDagen.map(dag => (
                      <tr key={dag.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{dag.dayName} {dag.formattedDate}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dag.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {dag.ritType === 'ov' ? (
                            <span className="text-green-700 font-medium">OV</span>
                          ) : (
                            `${dag.km.toFixed(2).replace('.', ',')} km`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {dag.ritType === 'ov'
                            ? formatCurrency(dag.bedrag)
                            : formatCurrency(dag.km * (userSettings.vergoedingPerKmZakelijk ?? 0.23))}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-purple-50 font-medium">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" colSpan="2">Totaal</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {displayTotalKmZakelijk > 0 ? `${displayTotalKmZakelijk.toFixed(2).replace('.', ',')} km` : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(displayTotalCompensationZakelijk)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                {zakelijkFilter === 'ov' ? 'Geen zakelijke OV-reizen geregistreerd voor deze maand.' :
                 zakelijkFilter === 'auto' ? 'Geen zakelijke auto- of fietsreizen geregistreerd voor deze maand.' :
                 'Geen zakelijke reizen geregistreerd voor deze maand.'}
                {zakelijkFilter === 'all' && <><br />Voeg zakelijke reizen toe via de registratiepagina.</>}
              </p>
            )}
          </>
        )}
      </div>
    </div>

    {copyToast && (
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl animate-fade-in">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">Gekopieerd naar klembord</span>
      </div>
    )}
    </>
  );
}

export default TravelCosts;